/**
 * csvExportUtils
 * ---------------
 * Shared helpers for converting schedule and booking datasets into downloadable CSV files.
 */
const sanitizeCsvValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const wrapCsvValue = (value) => {
  const sanitized = sanitizeCsvValue(value);
  if (sanitized === '') {
    return '';
  }
  const escaped = sanitized.replace(/"/g, '""');
  return /[",\r\n]/.test(escaped) ? `"${escaped}"` : escaped;
};

export const buildCsvContent = ({ columns = [], rows = [] } = {}) => {
  if (!columns.length) {
    return '';
  }

  const headerLine = columns.map((column) => wrapCsvValue(column.label || column.key || '')).join(',');

  if (!rows.length) {
    return headerLine;
  }

  const bodyLines = rows.map((row) =>
    columns
      .map((column) => {
        const value = typeof column.getter === 'function' ? column.getter(row) : row?.[column.key];
        return wrapCsvValue(value);
      })
      .join(',')
  );

  return [headerLine, ...bodyLines].join('\r\n');
};

export const downloadCsvFile = ({ filename = 'export.csv', csvContent } = {}) => {
  if (!csvContent || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};